import React from 'react';
import { ArrowRightOutlined } from '@ant-design/icons';
import RoleRotator from '../AIEmployees/sections/RoleRotator';
import BenchmarkBadge from '../Marketing/BenchmarkBadge';
import G2RatingPill from '../Marketing/G2RatingPill';
import { NYT_FEATURE } from '../../content/nytFeature';
import { trackEvent } from '../../utils/analytics';
import HomeDemoFrame from './HomeDemoFrame';
import { HERO } from './homeContent';
import './HomeFold.css';

/**
 * HomeFold — the homepage first screen (round-6 revamp, Aug 2026; layout H2,
 * Nitish's pick). Replaced the AIEHero + autoplay-video hero on "/".
 *
 * SHARED WITH /ai-employees since Sep 9 2026 (Nitish's direction, at the
 * desk): the same layout with that page's own copy. Props:
 *   content    the copy object (default homeContent.HERO): subMain, subLast,
 *              trust, cta, support. /ai-employees passes aieFoldContent.AIE_FOLD.
 *   badgePage  the benchmark pill's page attribution.
 *   demoPage   the demo frame's event attribution (compare_page / page).
 *   secondary  optional { label, icon, onClick } — a ghost button next to the
 *              primary. Home passes none (Aug 24 ruling: one control on home).
 *   band       paint the fold on the dark hero ground (var(--aie-hero-bg))
 *              fading into the page bg at the bottom, the way this page's
 *              hero always painted. Home stays on the page grey (untouched).
 *
 * Desktop (H2): two top-aligned columns —
 *   LEFT   the H1 (prod's exact markup: static aria-label + RoleRotator) +
 *          the locked sub-line (control sentence on its own line)
 *   RIGHT  the dated benchmark pill hugging the top-right corner, the three
 *          trust bullets directly beneath it, and the CTA ending the proof
 *          column ("reading the bullets lands on the button")
 *   BELOW  the interactive demo frame, full width.
 *
 * Mobile (<=700px): the columns flatten (display:contents) and CSS `order`
 * rebuilds the phone reading: badge → H1 → sub (one sentence per line) →
 * CTA → demo frame (peeking into screen one — the phone fold must show the
 * product) → bullets. ONE content layer, zero duplicated copy.
 *
 * ── THE CTA CLUSTER (Nitish, Aug 22) ─────────────────────────────────────
 * Was: "Hire your AI employee" + a co-equal ghost "Get a demo" opening a
 * modal. Measured over a month on prod: the ghost took 9 clickers and
 * converted ZERO, while the primary converted 15 of 26 — the textbook
 * secondary-cannibalization shape (a secondary absorbs 40-60% of primary
 * clicks at a fraction of the conversion).
 *
 * Now:
 *  - ONE decision, labelled "Get started". The activation fork qualifies
 *    intent one screen later, so the button's only job is opening the door;
 *    it also stops echoing the H1's own verb.
 *  - UPDATE (Nitish, Aug 24): the "See the demo" link was REMOVED entirely.
 *    7 days on prod: 5 link clicks / 3 sessions vs 36 clicks on the primary,
 *    while the demo frame itself drew 56 sessions of direct touches —
 *    visitors scroll to the demo, they don't click to it. The fold now
 *    carries ONE control ("Get started"); the demo frame and its own
 *    tracking are unchanged, and the `see_it_work` analytics series simply
 *    ends at the removal date (never renamed, never reused).
 *  - /ai-employees (Sep 9): that page keeps a secondary by his call ("See
 *    how it works", scrolls to the wall) — its own read, on its own page.
 *
 * SEO invariants: the H1 is byte-identical to prod's (aria-label + rotator +
 * prerendered "for any role." state). NO PRICE IN THE FOLD (Aug 17 ruling).
 * All copy from the content object — the copy assertion checks exactly that.
 */

/* Sentence-per-line on phones (his note: "They own the outcome" broke across
   lines and read as an afterthought). A LAYOUT split only: each sentence gets
   a span whose trailing space stays INSIDE it, so textContent is byte-identical
   to the locked copy; mobile CSS makes the spans block-level. */
const splitSentences = (s) => s.match(/[^.]+\.\s*/g) || [s];

/* The "See the demo" link and its spotlight machinery (tracked timer,
   unmount cleanup, sibling-fade classes) were removed with the link itself
   (Nitish, Aug 24) — see the CTA CLUSTER note above. The frame keeps its
   id="home-demo" anchor for deep links and its own tracking. */

const HomeFold = ({
  onPrimary,
  content = HERO,
  badgePage = 'home',
  demoPage = 'home',
  secondary = null,
  band = false,
}) => {
  const sentences = splitSentences(content.subMain);
  return (
    <section className={`home-fold${band ? ' home-fold--band' : ''}`}>
      <div className="home-fold__glow" aria-hidden="true" />
      <div className="home-fold__wrap">
        <div className="hf-grid">
          <div className="hf-left">
            {/* G2 rating (Nitish, Sep 27 2026): top-left, level with the
                DeepResearch pill on the right. Text only until G2 grants the
                logo (canon g2-claims). */}
            {/* The New York Times (Nitish, Oct 20 2026, CEL-3460; replaced the week-1 header
                ribbon): phones get this one quiet line above G2; wider screens get the pill
                beside G2 below. Text only, no NYT logo or lettering (Nora, CEL-3450). */}
            <a
              className="hf-nyt"
              href={NYT_FEATURE.url}
              target="_blank"
              rel="noopener noreferrer"
              onClick={() => trackEvent('cta_click', { cta_name: 'nyt_line', page: badgePage, location: 'hero' })}
            >
              <span>Featured in</span> <span className="hf-nyt__name">The New York Times</span>
              <ArrowRightOutlined />
            </a>
            <div className="hf-g2">
              <G2RatingPill page={badgePage}>
                <a
                  className="mkt-g2__pill hf-nyt-pill"
                  href={NYT_FEATURE.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  onClick={() => trackEvent('cta_click', { cta_name: 'nyt_pill', page: badgePage, location: 'hero' })}
                >
                  <span className="mkt-g2__on">Featured in</span>
                  <span>The New York Times</span>
                  <ArrowRightOutlined />
                </a>
              </G2RatingPill>
            </div>
            {/* Prod's H1, verbatim: stable aria-label = one static sentence
                for screen readers + crawlers while the accent line rotates.
                Prerendered visual state is identical to the label. */}
            <h1 className="home-fold__h1 aie-display" aria-label="Hire an AI employee for any role.">
              <span aria-hidden="true">
                Hire an AI employee
                <br />
                <RoleRotator />
              </span>
            </h1>
            <p className="hf-sub">
              {sentences.map((s, i) => (
                /* eslint-disable-next-line react/no-array-index-key */
                <span className="hf-sent" key={i}>{s}</span>
              ))}
              <span className="hf-sub__last">{content.subLast}</span>
            </p>
          </div>
          <div className="hf-right">
            <div className="hf-proof">
              <BenchmarkBadge page={badgePage} />
              <ul className="hf-trust">
                {content.trust.map((t) => <li key={t}>{t}</li>)}
              </ul>
            </div>

            <div className="hf-ctagroup">
              <div className="hf-ctarow">
                <button
                  type="button"
                  data-msc-suppress
                  className="aie-btn aie-btn-primary"
                  onClick={onPrimary}
                >
                  {content.cta}
                  <ArrowRightOutlined />
                </button>
                {secondary && (
                  <button
                    type="button"
                    className="aie-btn aie-btn-ghost"
                    onClick={secondary.onClick}
                  >
                    {secondary.icon}
                    {secondary.label}
                  </button>
                )}
              </div>
              <p className="hf-support">{content.support}</p>
            </div>
          </div>

          {/* The interactive product demo — full width under both columns on
              desktop, order 5 in the flattened phone column (it must peek
              into screen one: the phone fold shows the product, not text).
              It carries id="home-demo" as a stable deep-link anchor. */}
          <HomeDemoFrame page={demoPage} />
        </div>
      </div>
    </section>
  );
};

export default HomeFold;
